// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DataFilter } from './filter/filters-defaults';

type ViewType = 'grid' | 'table';

type DataFilterContextValue = {
  filter: DataFilter;
  setFilter: (filter: DataFilter) => void;
  viewType: ViewType;
  setViewType: (viewType: ViewType) => void;
};

const DEFAULT_FILTER: DataFilter = {
  dataset: [],
  processTag: [],
  label: [],
  imageTag: [],
};

const DataFilterContext = createContext<DataFilterContextValue | undefined>(undefined);

interface Props {
  projectId?: string;
  children: React.ReactNode;
}

export function DataFilterProvider({ projectId, children }: Readonly<Props>) {
  const [filter, setFilter] = useState<DataFilter>(() => ({ ...DEFAULT_FILTER }));
  const [viewType, setViewType] = useState<ViewType>('grid');

  useEffect(() => {
    setFilter({ ...DEFAULT_FILTER });
    setViewType('grid');
  }, [projectId]);

  const value = useMemo(
    () => ({
      filter,
      setFilter,
      viewType,
      setViewType,
    }),
    [filter, viewType]
  );

  return <DataFilterContext.Provider value={value}>{children}</DataFilterContext.Provider>;
}

export function useDataFilter() {
  const context = useContext(DataFilterContext);
  if (!context) {
    throw new Error('useDataFilter must be used within a DataFilterProvider');
  }
  return context;
}
